(() => {
  const RUNTIME_KEY = 'KalaleProductDetailRuntime';

  const initProductSection = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.productInitialized === 'true') return;
    section.dataset.productInitialized = 'true';

    const slider = section.querySelector('[data-gallery-slides]');
    const thumbs = Array.from(section.querySelectorAll('[data-gallery-thumbnails] .thumb'));
    const previousButton = section.querySelector('[data-gallery-previous]');
    const nextButton = section.querySelector('[data-gallery-next]');
    const slides = Array.from(section.querySelectorAll('.product-gallery__slide'));
    let currentIndex = 0;

    const pauseInactiveMedia = () => {
      slides.forEach((slide, index) => {
        if (index === currentIndex) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };

    const slideTo = (requestedIndex) => {
      if (!slider || thumbs.length === 0) return;

      let index = requestedIndex;
      if (index < 0) index = thumbs.length - 1;
      if (index >= thumbs.length) index = 0;
      currentIndex = index;

      slider.style.transform = `translateX(-${currentIndex * 100}%)`;
      thumbs.forEach((thumb) => thumb.classList.remove('is-active'));
      thumbs[currentIndex]?.classList.add('is-active');
      pauseInactiveMedia();
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number.parseInt(thumb.dataset.index || '', 10);
        if (Number.isInteger(index)) slideTo(index);
      });
    });
    previousButton?.addEventListener('click', () => slideTo(currentIndex - 1));
    nextButton?.addEventListener('click', () => slideTo(currentIndex + 1));

    const lightbox = section.querySelector('[data-gallery-lightbox]');
    const lightboxClose = section.querySelector('[data-lightbox-close]');
    const lightboxImage = section.querySelector('[data-lightbox-image]');
    const triggerImages = section.querySelectorAll('.zoomable-trigger-img');

    const closeLightbox = () => {
      if (!lightbox) return;
      lightbox.classList.remove('is-active');
      document.body.style.overflow = '';
    };

    triggerImages.forEach((image) => {
      image.addEventListener('click', () => {
        if (!lightbox || !(lightboxImage instanceof HTMLImageElement)) return;
        lightboxImage.src = image.dataset.src || image.src;
        lightboxImage.classList.remove('is-zoomed');
        lightboxImage.style.transform = 'none';
        lightbox.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });
    });

    lightboxClose?.addEventListener('click', closeLightbox);
    lightbox?.addEventListener('click', closeLightbox);

    if (lightboxImage instanceof HTMLImageElement) {
      lightboxImage.addEventListener('click', (event) => {
        event.stopPropagation();
        lightboxImage.classList.toggle('is-zoomed');
        lightboxImage.style.transform = lightboxImage.classList.contains('is-zoomed') ? 'scale(2.2)' : 'none';
      });

      lightboxImage.addEventListener('mousemove', (event) => {
        if (!lightboxImage.classList.contains('is-zoomed')) return;
        const rectangle = lightboxImage.getBoundingClientRect();
        const x = ((event.clientX - rectangle.left) / rectangle.width) * 100;
        const y = ((event.clientY - rectangle.top) / rectangle.height) * 100;
        lightboxImage.style.transformOrigin = `${x}% ${y}%`;
      });
    }

    const variantsNode = section.querySelector('[data-product-variants]');
    let productVariants = [];
    try {
      productVariants = JSON.parse(variantsNode?.textContent || '[]');
    } catch (error) {
      console.error('Unable to parse product variants.', error);
    }
    const initialVariant = productVariants.find((variant) => variant?.available) || productVariants[0];
    const initialVariantId = initialVariant?.id ? String(initialVariant.id) : '';

    const selectElement = section.querySelector('[data-variant-select]');
    const variantIdInput = section.querySelector('[data-product-variant-id]');
    const optionGroups = Array.from(section.querySelectorAll('.option-selector-group'));
    const priceElement = section.querySelector('[data-product-price]');
    const stickyPriceElement = section.querySelector('[data-sticky-price]');
    const comparePriceElement = section.querySelector('[data-product-compare-price]');
    const discountElement = section.querySelector('[data-product-discount]');

    if (selectElement && !selectElement.value && initialVariantId) {
      selectElement.value = initialVariantId;
    }
    if (variantIdInput && !variantIdInput.value && initialVariantId) {
      variantIdInput.value = initialVariantId;
    }

    const updateVariantDetails = () => {
      if (!selectElement) return;
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      if (!selectedOption) return;

      if (variantIdInput) variantIdInput.value = selectedOption.value;
      if (priceElement) priceElement.textContent = selectedOption.dataset.price || '';
      if (stickyPriceElement) stickyPriceElement.textContent = selectedOption.dataset.price || '';

      const priceValue = Number(selectedOption.dataset.priceVal || 0);
      const compareValue = Number(selectedOption.dataset.compareVal || 0);
      const isDiscounted = compareValue > priceValue && compareValue > 0;
      if (comparePriceElement) {
        comparePriceElement.textContent = selectedOption.dataset.compare || '';
        comparePriceElement.hidden = !isDiscounted;
      }
      if (discountElement) {
        const discountPercent = isDiscounted
          ? Math.round(((compareValue - priceValue) * 100) / compareValue)
          : 0;
        discountElement.textContent = `-${discountPercent}%`;
        discountElement.hidden = !isDiscounted;
      }
    };

    selectElement?.addEventListener('change', updateVariantDetails);

    optionGroups.forEach((group) => {
      const pills = Array.from(group.querySelectorAll('.variant-pill-button'));
      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          pills.forEach((candidate) => {
            candidate.classList.remove('is-active');
            candidate.setAttribute('aria-checked', 'false');
          });
          pill.classList.add('is-active');
          pill.setAttribute('aria-checked', 'true');

          const selectedValues = optionGroups.map((optionGroup) => {
            const activePill = optionGroup.querySelector('.variant-pill-button.is-active');
            return activePill?.dataset.value || '';
          });
          const matchedVariant = productVariants.find((variant) =>
            variant.options?.every((option, index) => String(selectedValues[index]) === String(option))
          );

          if (!matchedVariant || !selectElement) return;
          selectElement.value = String(matchedVariant.id);
          selectElement.dispatchEvent(new Event('change'));

          if (matchedVariant.featured_image) {
            const matchedSource = matchedVariant.featured_image.src;
            const matchedThumb = thumbs.find((thumb) => thumb.querySelector('img')?.src.includes(matchedSource));
            const matchedIndex = Number.parseInt(matchedThumb?.dataset.index || '', 10);
            if (Number.isInteger(matchedIndex)) slideTo(matchedIndex);
          }
        });
      });
    });

    updateVariantDetails();

    const productForm = section.querySelector('[data-product-form-container] form');
    if (productForm instanceof HTMLFormElement) {
      productForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submitButton = productForm.querySelector('[data-product-add-to-cart]');
        const originalText = submitButton?.innerHTML || 'Masukkan Keranjang';

        if (submitButton instanceof HTMLButtonElement) {
          submitButton.disabled = true;
          submitButton.innerHTML = '<span class="material-symbols-rounded" style="animation: spin 0.8s linear infinite; display: inline-block; vertical-align: middle; margin-right: 0.5rem;">sync</span> Memproses...';
        }

        try {
          const variantId = selectedVariantId();
          const quantity = selectedQuantity();
          if (!/^\d+$/.test(variantId)) {
            throw new Error('A valid product variant is required.');
          }

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity }]
            })
          });

          if (response.ok) {
            if (typeof window.triggerNotification === 'function') {
              window.triggerNotification('Produk berhasil ditambahkan ke keranjang!');
            }
            if (typeof window.updateShopifyCartBadge === 'function') {
              window.updateShopifyCartBadge();
            }
          } else {
            let errorData = {};
            try {
              errorData = await response.json();
            } catch (_error) {
              errorData = {};
            }
            if (typeof window.triggerNotification === 'function') {
              window.triggerNotification(`Gagal: ${errorData.description || 'Stok tidak mencukupi'}`);
            }
          }
        } catch (error) {
          console.error(error);
          if (typeof window.triggerNotification === 'function') {
            const message = error instanceof Error && error.message === 'A valid product variant is required.'
              ? 'Gagal: Varian produk tidak valid.'
              : 'Terjadi kesalahan koneksi.';
            window.triggerNotification(message);
          }
        } finally {
          if (submitButton instanceof HTMLButtonElement) {
            submitButton.disabled = false;
            submitButton.innerHTML = originalText;
          }
        }
      });
    }

    const quantityInput = section.querySelector('[data-quantity-input]');
    section.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => {
      if (!(quantityInput instanceof HTMLInputElement)) return;
      quantityInput.value = String(Math.max(1, Number(quantityInput.value) - 1));
    });
    section.querySelector('[data-quantity-increase]')?.addEventListener('click', () => {
      if (!(quantityInput instanceof HTMLInputElement)) return;
      quantityInput.value = String(Number(quantityInput.value) + 1);
    });

    const selectedVariantId = () => {
      if (selectElement?.value) return selectElement.value;
      if (variantIdInput?.value) return variantIdInput.value;
      const fallbackVariant = productVariants.find((variant) => variant?.available) || productVariants[0];
      return fallbackVariant?.id ? String(fallbackVariant.id) : '';
    };
    const selectedQuantity = () => quantityInput instanceof HTMLInputElement ? Math.max(1, Number(quantityInput.value) || 1) : 1;

    const addSelectedVariantAndCheckout = async () => {
      const variantId = selectedVariantId();
      if (!variantId) return;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: selectedQuantity() }] })
        });
        if (response.ok) window.location.href = '/checkout';
      } catch (error) {
        console.error(error);
      }
    };

    section.querySelector('[data-quick-buy]')?.addEventListener('click', addSelectedVariantAndCheckout);

    const stickyCartButton = section.querySelector('[data-sticky-cart]');
    stickyCartButton?.addEventListener('click', addSelectedVariantAndCheckout);

    const stickyBar = section.querySelector('[data-sticky-add]');
    const productInfo = section.querySelector('[data-product-info]');
    if (stickyBar && productInfo) {
      const observer = new IntersectionObserver(([entry]) => {
        const hasScrolledPastProductInfo = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        stickyBar.classList.toggle('is-visible', hasScrolledPastProductInfo);
      }, { threshold: 0 });
      observer.observe(productInfo);
    }
  };

  const initWithin = (root) => {
    if (root instanceof HTMLElement && root.matches('[data-product-section]')) initProductSection(root);
    root.querySelectorAll?.('[data-product-section]').forEach(initProductSection);
  };

  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].initWithin(document);
    return;
  }

  window[RUNTIME_KEY] = { initWithin };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
})();
